import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  `flex items-center justify-center gap-2 whitespace-nowrap font-bold transition-[color,background,border] outline-none cursor-pointer`,
  {
    variants: {
      variant: {
        primary: [
          'bg-brand-yellow',
          'text-brand-purple-black',
          'border-brand-yellow',
          'hover:bg-brand-yellow-cream',
          'hover:border-brand-yellow-cream',
          'focus-visible:ring-brand-purple',
          'focus-visible:ring-1',
          'focus-visible:border-2',
          'focus-visible:border-white',
        ],
        secondary: [
          'bg-white',
          'text-brand-purple-black',
          'border-brand-purple',
          'hover:bg-brand-purple',
          'hover:border-brand-purple',
          'hover:text-white',
          'focus-visible:shadow-button-secondary-focus',
          'focus-visible:border-2',
          'focus-visible:border-brand-purple',
        ],
        link: [
          'text-brand-purple',
          'max-w-fit',
          '[&_path]:fill-brand-purple',
          'focus-visible:border-b-[1px]',
          'focus-visible:border-brand-purple-black',
          'hover:text-brand-purple-black',
        ],
        carouselNav: [
          'bg-white',
          'border-brand-medium-gray',
          'border-2',
          'hover:bg-brand-purple-light',
          'hover:text-brand-purple-black-10',
          'focus-visible:shadow-button-secondary-focus',
          'focus-visible:border-2',
          'focus-visible:border-brand-purple-black-10',
          'z-10',
          'col-span-1',
          'flex',
          'rounded-full',
          'disabled:bg-brand-purple',
        ],
      },
      size: {
        small: ['text-sm'],
        medium: ['text-base'],
        icon: ['h-8', 'w-8', 'md:h-11', 'md:w-11'],
      },
      disabled: {
        false: null,
        true: ['pointer-events-none', '[&_svg]:pointer-events-none'],
      },
    },
    compoundVariants: [
      {
        variant: ['primary', 'secondary'],
        class: ['rounded-button', 'border-2', 'lg:max-w-fit', 'px-8', 'py-3'],
      },
      {
        variant: ['primary', 'secondary'],
        disabled: true,
        class: ['bg-brand-purple-black-10', 'border-brand-purple-black-10'],
      },
      {
        variant: 'primary',
        disabled: true,
        class: ['text-brand-purple-black-60'],
      },
      {
        variant: 'secondary',
        disabled: true,
        class: ['text-brand-purple-black-10'],
      },
      {
        variant: 'link',
        disabled: true,
        class: [
          'text-brand-purple-black-20',
          '[&_path]:fill-brand-purple-black-20',
        ],
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  },
);

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  chevronClassName?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      asChild = false,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const Component = asChild ? Slot : 'button';

    return (
      <Component
        data-slot="button"
        className={cn(buttonVariants({ variant, size, disabled }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';
